import React from 'react';
import { PaperClipOutlined } from '@ant-design/icons';
import Markdown from '@/components/ui/Markdown';
import StatusBadge from '@/components/ui/StatusBadge';
import {
    TaskCardSurface,
    TaskCardTopMetaRow,
    TaskCardTaskIdLabel,
    TaskCardHeadingTitle,
    TaskCardDescScroll,
    CardDescriptionInner,
    MutedText,
} from '@/pages/inside/tenant/style';
import { CLIENT_TICKET_COL, getClientTicketDisplayId, getClientTicketMarkdown } from '@hooks/projects/useProjectClientTickets';

function getClientTicketAttachmentItems(item) {
    const list = [
        ...(Array.isArray(item?.attachments) ? item.attachments : []),
        ...(Array.isArray(item?.attachmentUrls) ? item.attachmentUrls : []),
        ...(Array.isArray(item?.files)
            ? item.files
                .map((f) => ({
                    type: 'file',
                    url: f?.url || f?.downloadUrl || f?.path || '',
                    title: f?.name || f?.filename || 'file',
                }))
                .filter((x) => x.url)
            : []),
    ];
    return list.filter(Boolean);
}

function getClientTicketStatusBadge(status, displayColumnId) {
    const s = String(status ?? '').trim().toLowerCase();
    const col = String(displayColumnId ?? '').trim().toLowerCase();
    if (
        col === CLIENT_TICKET_COL.ACTIVE &&
        (s === CLIENT_TICKET_COL.SUBMITTED || s === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT)
    ) {
        return { key: 'in_progress', label: 'In Progress' };
    }
    if (s === 'acknowledged') return { key: 'acknowledged', label: 'Acknowledged' };
    if (s === 'submitted' || s === 'submitted_to_client') return { key: 'submitted', label: 'Submitted' };
    return { key: 'active', label: 'Active' };
}

const attachmentChipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 8,
    border: '1px solid var(--ant-color-border-secondary, #e5e7eb)',
    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1,
    color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
    flexShrink: 0,
};

export default function ClientTicketCard({ item, onSelect, displayColumnId }) {
    const attachmentCount = getClientTicketAttachmentItems(item).length;
    const title = String(item?.title || '').trim() || 'Client Ticket';
    const markdown = getClientTicketMarkdown(item);
    const ticketState = String(item?.state ?? '').trim().toLowerCase();
    const isImproper = ticketState === 'improper';
    const hasClassificationBadge = ticketState === 'genuine' || ticketState === 'improper';
    const reason = String(item?.reason ?? '').trim();
    const statusBadge = getClientTicketStatusBadge(item?.status, displayColumnId);
    const showStatusBadge = !(hasClassificationBadge && statusBadge.key === 'submitted');

    return (
        <TaskCardSurface
            $kanbanGrab
            $kanbanMuteHover
            $kanbanColumn="todo"
            onClick={onSelect ? () => onSelect(item) : undefined}
            style={{ cursor: onSelect ? 'pointer' : 'default' }}
        >
            <TaskCardTopMetaRow style={{ marginBottom: 6, justifyContent: 'space-between', gap: 8 }}>
                <TaskCardTaskIdLabel>{getClientTicketDisplayId(item)}</TaskCardTaskIdLabel>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {showStatusBadge ? (
                        <StatusBadge status={statusBadge.key} size="small">
                            {statusBadge.label}
                        </StatusBadge>
                    ) : null}
                    {hasClassificationBadge ? (
                        <StatusBadge status={ticketState} size="small" />
                    ) : null}
                </div>
            </TaskCardTopMetaRow>
            <TaskCardHeadingTitle $lineClamp1 style={{ marginBottom: 6 }}>
                {title}
            </TaskCardHeadingTitle>
            {markdown ? (
                <TaskCardDescScroll $kanbanColumn="todo" $previewLines={4}>
                    <Markdown
                        source={markdown}
                        component={CardDescriptionInner}
                        variant="compact"
                        lineClamp={4}
                        linkPrimary
                        richInlineAnchors
                        attachmentItems={getClientTicketAttachmentItems(item)}
                    />
                </TaskCardDescScroll>
            ) : (
                <MutedText style={{ fontSize: 12, margin: 0 }}>No description.</MutedText>
            )}
            {isImproper && reason ? (
                <div style={{ marginTop: 8 }}>
                    <MutedText style={{ fontSize: 12, margin: '0 0 4px 0' }}>Reason</MutedText>
                    <Markdown source={reason} component={CardDescriptionInner} variant="compact" lineClamp={3} />
                </div>
            ) : null}
            {attachmentCount > 0 ? (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        marginTop: 0,
                        paddingTop: 2,
                        borderTop: '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.08))',
                        width: '100%',
                    }}
                >
                    <span style={{ ...attachmentChipStyle, pointerEvents: 'none' }} aria-hidden>
                        <PaperClipOutlined style={{ fontSize: 12 }} />
                        {attachmentCount}
                    </span>
                </div>
            ) : null}
        </TaskCardSurface>
    );
}
